/**
 * Native Services for Stay Kool Running
 * Handles background GPS tracking, health data sync, and push notifications
 * 
 * IMPORTANT: These features only work when the app is built as a native iOS/Android app
 * using Capacitor. They will not work in web browsers.
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

// Check if running on native platform
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Background Geolocation Service
 * Tracks GPS location even when app is minimized
 */
export const BackgroundLocationService = {
  watcher: null,
  callbacks: [],
  
  /**
   * Check if background location is available
   */
  isAvailable: () => isNative(),
  
  /**
   * Start background location tracking
   * @param {Function} onLocation - Callback when location updates
   * @param {Function} onError - Callback on error
   */
  start: async (onLocation, onError) => {
    if (!isNative()) {
      console.log('Background location only available on native platforms');
      return false;
    }
    
    try {
      // Dynamic import with webpackIgnore to prevent bundling native-only code
      const BackgroundGeolocation = await import(
        /* webpackIgnore: true */
        '@capacitor-community/background-geolocation'
      ).then(m => m.BackgroundGeolocation).catch(() => null);
      
      if (!BackgroundGeolocation) {
        console.error('Background geolocation plugin not available');
        if (onError) onError({ message: 'Background geolocation not available' });
        return false;
      }
      
      // Add watcher for background location updates
      BackgroundLocationService.watcher = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: "Stay Kool Running is tracking your run",
          backgroundTitle: "Running in Background",
          requestPermissions: true,
          stale: false,
          distanceFilter: 10, // Update every 10 meters
        },
        (location, error) => {
          if (error) {
            console.error('Background location error:', error);
            if (onError) onError(error);
            return;
          }
          
          if (location && onLocation) {
            onLocation({
              lat: location.latitude,
              lng: location.longitude,
              accuracy: location.accuracy,
              altitude: location.altitude,
              speed: location.speed,
              timestamp: new Date().toISOString()
            });
          }
        }
      );
      
      console.log('Background location tracking started');
      return true;
    } catch (error) {
      console.error('Failed to start background location:', error);
      if (onError) onError(error);
      return false;
    }
  },
  
  /**
   * Stop background location tracking
   */
  stop: async () => {
    if (!isNative()) return;
    
    try {
      const BackgroundGeolocation = await import(
        /* webpackIgnore: true */
        '@capacitor-community/background-geolocation'
      ).then(m => m.BackgroundGeolocation).catch(() => null);
      
      if (BackgroundGeolocation && BackgroundLocationService.watcher) {
        await BackgroundGeolocation.removeWatcher({
          id: BackgroundLocationService.watcher
        });
        BackgroundLocationService.watcher = null;
        console.log('Background location tracking stopped');
      }
    } catch (error) {
      console.error('Failed to stop background location:', error);
    }
  }
};

/**
 * Health Data Service
 * Syncs step data with Apple HealthKit (iOS) and Health Connect (Android)
 */
export const HealthService = {
  isAuthorized: false,
  
  /**
   * Check if health data is available
   */
  isAvailable: () => isNative(),
  
  /**
   * Request authorization to read/write health data
   */
  requestAuthorization: async () => {
    if (!isNative()) {
      console.log('Health data only available on native platforms');
      return false;
    }
    
    try {
      const { CapacitorHealth } = await import('@capgo/capacitor-health');
      
      // Request authorization for steps
      const result = await CapacitorHealth.requestAuthorization({
        read: ['steps', 'distance', 'calories'],
        write: ['steps', 'distance', 'workout']
      });
      
      HealthService.isAuthorized = result.authorized;
      console.log('Health authorization:', result);
      return result.authorized;
    } catch (error) {
      console.error('Failed to request health authorization:', error);
      return false;
    }
  },
  
  /**
   * Get steps for a date range
   * @param {Date} startDate - Start of range
   * @param {Date} endDate - End of range
   */
  getSteps: async (startDate, endDate) => {
    if (!isNative() || !HealthService.isAuthorized) {
      return null;
    }
    
    try {
      const { CapacitorHealth } = await import('@capgo/capacitor-health');
      
      const result = await CapacitorHealth.queryAggregated({
        dataType: 'steps',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      return result.value || 0;
    } catch (error) {
      console.error('Failed to get steps:', error);
      return null;
    }
  },
  
  /**
   * Get today's steps
   */
  getTodaySteps: async () => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return HealthService.getSteps(startOfDay, now);
  },
  
  /**
   * Save a workout to health store
   * @param {Object} workout - Workout data
   */
  saveWorkout: async (workout) => {
    if (!isNative() || !HealthService.isAuthorized) {
      return false;
    }
    
    try {
      const { CapacitorHealth } = await import('@capgo/capacitor-health');
      
      await CapacitorHealth.store({
        dataType: 'workout',
        value: {
          workoutType: workout.activity_type === 'run' ? 'running' : 'walking',
          startDate: workout.startDate || new Date().toISOString(),
          endDate: workout.endDate || new Date().toISOString(),
          duration: workout.duration, // seconds
          distance: workout.distance * 1609.34, // miles to meters
          calories: workout.calories || 0
        }
      });
      
      console.log('Workout saved to health store');
      return true;
    } catch (error) {
      console.error('Failed to save workout:', error);
      return false;
    }
  }
};

/**
 * Step Sync Service
 * Automatically syncs steps from Health to active step battle challenges
 */
export const StepSyncService = {
  syncInterval: null,
  lastSyncTime: null,
  apiUrl: null,
  authToken: null,
  
  /**
   * Initialize step sync with API credentials
   */
  initialize: (apiUrl, getAuthToken) => {
    StepSyncService.apiUrl = apiUrl;
    StepSyncService.getAuthToken = getAuthToken;
  },
  
  /**
   * Start automatic step syncing
   * @param {number} intervalMs - Sync interval in milliseconds (default: 5 minutes)
   */
  startAutoSync: async (intervalMs = 300000) => {
    if (!isNative()) {
      console.log('Step sync only available on native platforms');
      return false;
    }
    
    // Request health authorization first
    const authorized = await HealthService.requestAuthorization();
    if (!authorized) {
      console.log('Health authorization required for step sync');
      return false;
    }
    
    // Do initial sync
    await StepSyncService.syncNow();
    
    // Set up interval
    StepSyncService.syncInterval = setInterval(() => {
      StepSyncService.syncNow();
    }, intervalMs);
    
    console.log(`Step sync started with ${intervalMs/1000}s interval`);
    return true;
  },
  
  /**
   * Stop automatic step syncing
   */
  stopAutoSync: () => {
    if (StepSyncService.syncInterval) {
      clearInterval(StepSyncService.syncInterval);
      StepSyncService.syncInterval = null;
    }
  },
  
  /**
   * Sync steps now
   * Returns the synced step count or null on failure
   */
  syncNow: async () => {
    if (!isNative() || !HealthService.isAuthorized) {
      return null;
    }
    
    try {
      // Get today's steps from health
      const steps = await HealthService.getTodaySteps();
      if (steps === null) return null;
      
      console.log(`Syncing ${steps} steps to challenges`);
      
      // Get auth token
      const token = StepSyncService.getAuthToken ? StepSyncService.getAuthToken() : null;
      if (!token || !StepSyncService.apiUrl) {
        console.log('No auth token or API URL for step sync');
        return steps;
      }
      
      // Sync to all active step battle challenges
      const response = await fetch(`${StepSyncService.apiUrl}/api/user/sync-steps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ steps })
      });
      
      if (response.ok) {
        StepSyncService.lastSyncTime = new Date();
        console.log('Steps synced successfully');
      }
      
      return steps;
    } catch (error) {
      console.error('Failed to sync steps:', error);
      return null;
    }
  },
  
  /**
   * Get last sync time
   */
  getLastSyncTime: () => StepSyncService.lastSyncTime
};

/**
 * Push Notifications Service
 * Handles workout reminders and achievement notifications
 */
export const PushNotificationService = {
  token: null,
  listeners: [],
  
  /**
   * Check if push notifications are available
   */
  isAvailable: () => isNative(),
  
  /**
   * Initialize push notifications
   * @param {Function} onNotification - Callback when notification received
   * @param {Function} onToken - Callback when token received
   */
  initialize: async (onNotification, onToken) => {
    if (!isNative()) {
      console.log('Push notifications only available on native platforms');
      return null;
    }
    
    try {
      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      
      if (permResult.receive !== 'granted') {
        console.log('Push notification permission denied');
        return null;
      }
      
      // Register with APNs/FCM
      await PushNotifications.register();
      
      // Listen for registration token
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration token:', token.value);
        PushNotificationService.token = token.value;
        if (onToken) onToken(token.value);
      });
      
      // Listen for registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
      });
      
      // Listen for received notifications
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
        if (onNotification) onNotification(notification);
      });
      
      // Listen for notification actions
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('Push notification action:', action);
        if (onNotification) onNotification(action.notification, action);
      });
      
      console.log('Push notifications initialized');
      return PushNotificationService.token;
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      return null;
    }
  },
  
  /**
   * Get the current FCM/APNs token
   */
  getToken: () => PushNotificationService.token,
  
  /**
   * Remove all listeners
   */
  cleanup: () => {
    PushNotifications.removeAllListeners();
  }
};

/**
 * Initialize all native services
 * Call this when the app starts
 */
export const initializeNativeServices = async () => {
  if (!isNative()) {
    console.log('Running in web mode - native services disabled');
    return {
      backgroundLocation: false,
      health: false,
      pushNotifications: false,
      platform: 'web'
    };
  }
  
  const platform = getPlatform();
  console.log(`Initializing native services for ${platform}`);
  
  const results = {
    platform,
    backgroundLocation: BackgroundLocationService.isAvailable(),
    health: false,
    pushNotifications: false
  };
  
  // Initialize health service
  try {
    results.health = await HealthService.requestAuthorization();
  } catch (e) {
    console.error('Health init failed:', e);
  }
  
  // Initialize push notifications (don't auto-request, let user trigger)
  results.pushNotifications = PushNotificationService.isAvailable();
  
  return results;
};

export default {
  isNative,
  getPlatform,
  BackgroundLocationService,
  HealthService,
  PushNotificationService,
  initializeNativeServices
};
